import axios from 'axios'
import moment from "moment"
import pluralize from 'pluralize'
import { io } from "socket.io-client"
import { useSnackbar } from 'notistack'
import { Dropdown, Menu, message } from 'antd'
import { useLocation } from "react-router-dom"
import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { logout, setCodeBlue, setShift } from "./store/actions"
import { inferFieldDataSourceKey, getFieldSourceLabel } from "./formDataSources"

import Reports from "./components/pages/Reports"
import Confirm from "./components/reusable/confirm"
import Inventory from "./components/pages/Inventory"
import Pagination from "./components/reusable/pagination"
import OngoingShift from "./components/modals/ongoingshift"

import beep from './sounds/beep.mp3'
import code_blue from './sounds/code_blue.mp3'

// export const host = localStorage.getItem('host')

// export const port = localStorage.getItem('port')

export const production = process.env.REACT_APP_ENV === 'production'

export const server = process.env.REACT_APP_API_URL || 'http://localhost:5400'
export const icdServer = process.env.REACT_APP_ICD_API_URL || 'https://icd11restapi-developer-test.azurewebsites.net'
export const icdRelease = process.env.REACT_APP_ICD_RELEASE || '2026-01'

export const ws = io(server)

export const useToggle = (initialState = false) => {
    const [state, setState] = useState(initialState)

    const toggle = useCallback(() => setState(state => !state), [])

    return [state, toggle, setState]
}

export const useLatestPathname = () => {
    const location = useLocation()
    const pathnameRef = useRef(location.pathname)
    pathnameRef.current = location.pathname
    return pathnameRef
}

export const useQuery = () => {
    const { search } = useLocation()

    return useMemo(() => new URLSearchParams(search), [search])
}

export const trimText = (text, length = 30) => text?.length > length ? `${text?.substr(0, length)}...` : text

export const presets = {
    'Today': [moment().startOf('day'), moment().endOf('day')],
    'Yesterday': [moment().subtract(1, 'day').startOf('day'), moment().subtract(1, 'day').endOf('day')],
    'This Week': [moment().startOf('week'), moment().endOf('week')],
    'Last Week': [moment().startOf('week').subtract(1, 'week'), moment().startOf('week').subtract(1, 'week').endOf('week')],
    'This Month': [moment().startOf('month'), moment().endOf('month')],
    'Last Month': [moment().startOf('month').subtract(1, 'month'), moment().startOf('month').subtract(1, 'month').endOf('month')],
}

export const getInputProps = field => {
    let { name, unit, type, options, payload, endpoint, labelProperty, valueProperty, createEndpoint } = field
    const dataSourceKey = inferFieldDataSourceKey(field)
    let props = { 
        name,
        type: { label: type, value: type },
        source: null,
        unit,
        labelProperty,
        valueProperty,
        options,
        endpoint,
        dataSourceKey,
        createEndpoint: null,
        creatable: false, 
        payload
    }

    if (type.includes('Select') || type.includes('Searchable')) {
        let source = getFieldSourceLabel(field)
        const rawType = type
        type = type.includes('Multi') ? 'Multi Select' : 'Select'
        if (rawType.includes('Creatable')) {
            props.creatable = true
            props.createEndpoint = createEndpoint
        }

        props.type = { label: type, value: type }
        props.source = { label: source, value: source }
    }

    return props
}

export const parseInstructions = (inst = '1 x 1 for 1 day') => {
    inst = inst.split(' ')

    let dsg = 1
    let freq = 1
    let dys = 1

    let prev
    for (let char of inst) {
        if (char === 'x') {
            dsg = prev
        } else if (char === 'for') {
            freq = prev
        } else if (char.includes('day')) {
            dys = prev
        }

        prev = char
    }

    return [dsg, freq, dys]
}

export const useSocketEvents = events => {
    const { enqueueSnackbar } = useSnackbar()
    const { user } = useSelector(state => state.application)
    // const [play] = useSound(beep)
    const audio = useMemo(() => new Audio(beep), [])
    const eventsRef = useRef(events)
    const eventNames = Object.keys(events).sort()
    const eventKey = eventNames.join(',')

    useEffect(() => {
        eventsRef.current = events
    }, [events])

    useEffect(() => {
        if (!user) return

        const subscriptions = eventNames.map(eventName => {
            const handler = data => {
                let { callback, notif, condition = () => true } = eventsRef.current[eventName] ?? {}

                if (!callback) return

                if (condition(data)) {
                    if (notif) {
                        const notificationMessage =
                            typeof notif?.message === 'function' ? notif.message(data) : (notif?.message ?? data.message)
                        audio.play()
                        enqueueSnackbar(notificationMessage, notifOps(notif?.type ?? data?.type ?? 'success'))
                    }
                    callback(data)
                }
            }

            ws.on(eventName, handler)

            return { eventName, handler }
        })

        return () => {
            subscriptions.forEach(({ eventName, handler }) => {
                ws.off(eventName, handler)
            })
        }
    }, [audio, enqueueSnackbar, eventKey, user])
}

export const useCodeBlue = code => {
    const dispatch = useDispatch()

    ws.on('code-blue', data => {
        if (data.code !== code)
            dispatch(setCodeBlue(data))
    })
    ws.on('stop-code-blue', () => dispatch(setCodeBlue()))
}

export const useShift = department => {
    const [ongoing, setShifts] = useState([])

    const { shift } = useSelector(state => state.application)
    
    const dispatch = useDispatch()

    const [shiftModal, toggle] = useToggle()
  
    const [, openShifts] = usePet('openShift', { department }, {
        notify: true, callback: async ({ data }) => {
            setShifts(data ? [data] : [])
            dispatch(setShift(data))
        }, manual: (!department || shift) ? true : false
    })

    const [, start] = usePet('startShift', { department }, {
        notify: true, callback: ({ data }, notif, ops) => {
            dispatch(setShift(data))
            openShifts()
            notif('Shift has been started', ops('success'))
        }, manual: true, load: { msg: 'Starting new shift...' }
    })

    const [, end] = usePet('endShift', {
        id: shift?._id
    }, {
        notify: true, callback: ({ data }, notif, ops) => {
            dispatch(setShift(null))
            openShifts()
            notif('Shift has been ended', ops('success'))
        }, manual: true, load: { msg: 'Ending shift...' }
    })

    const Modal = shiftModal && <OngoingShift
        setShift={setShift}
        shifts={ongoing}
        toggle={toggle}
        visible={shiftModal} />

    const startShift = () => Confirm({
        title: 'Start New Shift?',
        message: ongoing.length > 0 ? "This will stop the current shift and start a new one" : "Are you sure you want start a new shift?",
        okText: 'Start Shift',
        onOk: start
    })

    const endShift = () => Confirm({
        title: 'End Current Shift?',
        message: "Please confirm that you would like to end this shift？",
        okText: 'End Shift',
        onOk: end
    })

    const Shifts = ({ }) => 
        <Dropdown
            overlay={
                <Menu>
                    {
                        ongoing?.length > 0 && !shift &&
                        <Menu.Item onClick={toggle}>View Ongoing Shifts</Menu.Item>
                    }
                    { 
                        shift ? <Menu.Item onClick={ endShift }>End Shift</Menu.Item> 
                        : <Menu.Item onClick={ startShift }>Start New Shift</Menu.Item>
                    }
                </Menu>
            }>
            <button className={`btn ${shift ? `bg-green-400 hover:bg-green-500` : 'bg-yellow-400 hover:bg-yellow-600'} ml-2 text-white`}>
                <span className="hidden xs:block">{shift ? `${shift?.user?.name}'s Shift Ongoing` : 'Select Shift'}</span>
            </button>
        </Dropdown>

    return [Shifts, Modal]
}

export const useInventory = storeName => {
    const [requests, setRequests] = useState(0)
    const [store, setStore] = useState()
    const [authorized, setAuthorized] = useState(false)

    const [, getRequests] = usePet('requestsByStore', { store: store?._id, type: "Received", classif: 'Pending' }, { notify: true, manual: true, callback: ({ data }) => setRequests(data.count) })

    usePet('getStoreId', { name: storeName }, {
        notify: false, callback: ({ data }) => {
            if (data) {
                setStore(data)
                setTimeout(getRequests, 100)
            } else {
                setAuthorized(false)
            }
        }
    })

    useSocketEvents({
        'new-request': {
            condition: data => data.store === storeName,
            notif: {
                message: 'New Store Request',
                type: 'info'
            },
            callback: () => null //getRequests
        }
    })

    return [Inventory, store, requests, authorized]
}

export const useReports = department => {
    const [reports, setReports] = useState([])

    usePet('reportTemplates', { department, limit: 100 }, { notify: true, callback: ({ data }) => setReports(data.templates), manual: false })
    
    return [Reports, reports]
}

export const isValidJson = str => {
    try {
        JSON.parse(str)
    } catch (e) {
        return false
    }
    return true
}

export const plural = (unit, count) => pluralize(unit, count)

export const Close = ({ onClick }) => (
    <button
        className="close-notif"
        onClick={() => onClick()}>
        Close
    </button>
)

export const notifOps = (type, action = null, persist = false, duration = 3000) => ({
    variant: type,
    persist,
    autoHideDuration: persist ? null : duration,
    action,
    preventDuplicate: true
})

export const stringifyDate = (date, noTime = false) => {
    let d = new Date(date).toString()
    d = d.slice(0, d.indexOf(' GMT'))

    if (noTime)
        d = d.slice(0, d.lastIndexOf(' '))

    return d
}

export const stringifyTime = (date, noSecs = false) => {
    let d = moment(date).format("HH:mm")

    return d
}

export let getExtension = name => {
    let split = name.split('.')
    let ext = split[split.length - 1]

    return ext
}

export const normalize = (val, nearest = 100) => (Math.round(val * nearest) / nearest)

export const getProcurementInvoiceItemCount = item => {
    const bulkCount = Number(item?.bulkCount ?? item?.bulk ?? 0)
    const containing = Number(item?.containing ?? item?.contains ?? 0)

    if (typeof item?.singles === 'boolean')
        return Math.abs(item.singles ? bulkCount : bulkCount * containing)

    if (item?.bulk !== undefined || item?.contains !== undefined)
        return Math.abs((bulkCount * containing) === containing ? (bulkCount * containing) : bulkCount)

    return Math.abs(bulkCount * containing)
}

export const summarizeProcurementInvoiceItem = item => {
    const count = getProcurementInvoiceItemCount(item)
    const unitPrice = Number(item?.bp ?? item?.price ?? 0)
    const discountPerUnit = Number(item?.discount ?? 0)
    const vatRate = Number(item?.vat ?? 0)
    const beforeDiscount = count * unitPrice
    const discount = count * discountPerUnit
    const beforeTax = count * (unitPrice - discountPerUnit)
    const vat = beforeTax * (vatRate / 100)
    const total = beforeTax + vat

    return {
        count,
        beforeDiscount,
        discount,
        beforeTax,
        vat,
        total,
    }
}

export const summarizeProcurementInvoice = (items = [], amountPaid = 0, amountCredited = 0) => {
    const summary = (items ?? []).reduce((totals, item) => {
        const line = summarizeProcurementInvoiceItem(item)
        totals.beforeDiscount += line.beforeDiscount
        totals.discount += line.discount
        totals.beforeTax += line.beforeTax
        totals.vat += line.vat
        totals.total += line.total
        return totals
    }, {
        beforeDiscount: 0,
        discount: 0,
        beforeTax: 0,
        vat: 0,
        total: 0,
    })

    return {
        ...summary,
        amountPaid: Number(amountPaid ?? 0),
        amountCredited: Number(amountCredited ?? 0),
        outstanding: Math.max(summary.total - Number(amountPaid ?? 0) - Number(amountCredited ?? 0), 0),
    }
}

export const normalizeCase = (string, ) => {
    string = string.split(' ')
    let final = []

    for (let name of string) {
        name = name.split('')
        // name[0] = name.toUpperCase()
        name = name.map((char, i) => i === 0 ? char.toUpperCase() : char.toLowerCase())

        final.push(name.join(''))
    }
        // final.push(name.replace(name.charAt(0), name.charAt(0).toUpperCase()))

    return final.join(' ')
}

export const permissions = (user, station, permission = '*') => user.issuperuser || user.stations.some(stat => stat.name.toLowerCase() === station.toLowerCase() && (stat.permissions.includes('*') || stat.permissions.includes(permission)))

export const normalizeLinkCase = string => {
    if (string === 'credit-patient-docs')
        return 'Credit Patient Docs'

    // Display-only rename: the `optical` station slug stays the data key, but the nav label
    // reads "Eye Clinic" (see docs/planning/EYE_CLINIC_OUTREACH_SCOPE.md §3.1).
    if (string === 'optical')
        return 'Eye Clinic'

    let re = new RegExp('-', 'g');
    string = string.replace(re, ' ');

    return normalizeCase(string)
}

export const camelize = str => {
    return str.replace(/(?:^\w|[A-Z]|\b\w)/g, function (word, index) {
        return index === 0 ? word.toLowerCase() : word.toUpperCase();
    }).replace(/\s+/g, '')
}

export const conversionRate = rate => {
    if (!isNaN(rate))
        return Number(rate)

    if (rate.includes('/'))
        return eval(rate)

    return 1
}

// usePet's execute(overridePayload) is meant for callers that need to pass a one-off payload
// (e.g. execute({ id: 5 })), but `onClick={ execute }` (no wrapper arrow) is an extremely easy
// mistake - React calls click handlers with the click's SyntheticEvent, which then becomes
// `overridePayload` and gets spread into the request body. That event holds a reference to the
// clicked DOM node, which holds a circular React Fiber reference back to itself, so
// JSON-serializing the request throws "Converting circular structure to JSON" instead of
// sending anything. Detect and ignore event-shaped payloads here so a call site with this
// mistake fails safe (falls back to the hook's own payload) instead of corrupting the request.
const isDomEventLike = value =>
    (typeof Event !== 'undefined' && value instanceof Event) ||
    (value && typeof value === 'object' && typeof value.preventDefault === 'function' && (value.nativeEvent || value.target))

export const usePet = (endpoint = '', payload, { url = `${server}/api`, notify, callback = () => null, manual, load, hasFile, method = 'post', logOutOnError = false, token: tkn, bypassLock = false, headers: hdrs }) => {
    load = load ? {
        msg: load.msg || 'loading'
    } : null

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [data, setData] = useState(null)
    const [lock, setLock] = useState(manual ? true : false)
    const payloadRef = useRef(payload)
    const callbackRef = useRef(callback)
    const tokenRef = useRef(tkn)
    const headersRef = useRef(hdrs)
    const nextPayloadRef = useRef()
    // request() below is async - if the component unmounts while the axios call is still in
    // flight (very common during dev when React Fast Refresh remounts a tree mid-request),
    // the setState calls after `await` fire on an unmounted component. React logs that via
    // console.error, and react-error-overlay (react-scripts' dev error overlay) pops a
    // full-screen block for any console.error, not just real crashes - so a harmless dev-only
    // warning here was surfacing as a broken, unclosable overlay on basically every hot reload.
    const mountedRef = useRef(true)
    useEffect(() => {
        mountedRef.current = true
        return () => { mountedRef.current = false }
    }, [])

    const { enqueueSnackbar, closeSnackbar } = useSnackbar()

    const { user } = useSelector(state => state?.application)

    const dispatch = useDispatch()

    useEffect(() => {
        payloadRef.current = payload
        callbackRef.current = callback
        tokenRef.current = tkn
        headersRef.current = hdrs
    }, [payload, callback, tkn, hdrs])

    const kickOut = () => dispatch(logout())

    const errorNotif = msg => enqueueSnackbar(msg, notifOps('warning', <Close onClick={closeSnackbar} />, false, 3000))

    const request = async (overridePayload) => {
        let token = sessionStorage.getItem('token')
        if (!bypassLock)
            if (!token && user) {
                errorNotif('Your session has been locked. Please log in again to continue')
                return
            }

        const activePayload = overridePayload ?? payloadRef.current ?? {}
        const form = hasFile ? new FormData() : {}

        for (let item in activePayload) {
            if (hasFile) {
                if (Array.isArray(activePayload[item])) {
                    if (activePayload[item].length > 0 && activePayload[item][0] instanceof File)
                        activePayload[item].forEach(file => form.append(item, file))
                    else
                        form.append(item, JSON.stringify(activePayload[item]))
                } else {
                    if (activePayload[item])
                        form.append(item, activePayload[item])
                }
            } else {
                form[item] = activePayload[item]
            }
        }

        if (hasFile) {
            form.append('hasFile', true)
            form.append('timeStamp', new Date())
        } else {
            form.timeStamp = new Date()
        }

        let headers
        const activeHeaders = headersRef.current

        if (token || activeHeaders) {
            headers = {}

            if (token)
                headers.Authorization = `Bearer ${tokenRef.current ?? token}`

            if (activeHeaders)
                headers = { ...headers, ...activeHeaders}
        }

        try {
            setLoading(true)
            setError(false)
            let key
            if (manual && load)
                key = message.loading(load.msg)
            // key = enqueueSnackbar(loadingMsg, notifOps('info', loader, true))

            let response = await axios({
                url: `${url}/${endpoint}`,
                method,
                data: form,
                headers
            })

            if (manual && load)
                key()
            // closeSnackbar(key)

            let { data: received } = response

            // Only the local state/callback need the mounted guard - errorNotif (a global
            // snackbar) and kickOut (a redux dispatch) below aren't tied to this component's
            // lifecycle and should still run even if it unmounted mid-request.
            if (mountedRef.current) {
                setLoading(false)
                setData(received.data)
                callbackRef.current(received, enqueueSnackbar, notifOps, closeSnackbar)
            }
        } catch (err) {
            log({ err })

            if (mountedRef.current) {
                setLoading(false)
                setError(err)
            }

            if (notify) {
                if (err.response) {
                    if (err.response.data) {
                        if (err.response.data.message) {
                            errorNotif(err.response.data.message)
                        } else {
                            errorNotif(err.message)
                        }
                    } else {
                        errorNotif('An error occurred at our servers, please contact the admin.')
                    }
                } else if (err.request) {
                    errorNotif('We seem to be offline at the moment, please contact the admin.')
                } else {
                    // Neither err.response nor err.request means axios never got as far as
                    // sending anything - the failure happened locally while building the
                    // request (a bad payload, a thrown error in a header/interceptor, etc).
                    // That's not a connectivity problem, so don't claim it's one - show what
                    // actually broke.
                    errorNotif(err.message || 'Something went wrong before the request could be sent')
                }
            }

            if (err.response?.data?.kickOut || logOutOnError)
                kickOut()
        }
    }

    const makeRequest = async () => {
        if (!lock) {
            setLock(true)
            request(nextPayloadRef.current)
            nextPayloadRef.current = undefined
        }
    }

    // This effect alone already covers the auto-fire-on-mount case: non-manual hooks start
    // with lock=false, so this runs makeRequest() once right after mount, same as manual hooks
    // starting lock=true and only firing once execute() flips it. There used to also be a
    // `if (!manual) makeRequest()` call directly in the render body here - calling it there
    // fired the request synchronously during render *and* this effect fired it again right
    // after, both using the same stale (pre-setLock) `lock` closure, so every non-manual
    // usePet call double-fired its request on mount. Side effects belong in effects, not the
    // render body - removing that call fixes the duplicate without changing any behavior.
    useEffect(makeRequest, [lock])

    const execute = (overridePayload) => {
        nextPayloadRef.current = isDomEventLike(overridePayload) ? undefined : overridePayload
        setLock(false)
    }

    return [{ loading, error, data }, execute]
}

export const range = (start, end) => {
    const result = [];
    for (let i = start; i < end; i++) {
      result.push(i);
    }
    return result;
}

export const pharmacyStatus = (order, group) => {
    let main = 'Not Paid'
    let colour = 'yellow'
    let status = ''

    let items = order?.items?.filter(item => group?.categories?.indexOf(item?.ref?.category) >= 0)
    if (items.some(item => !item.served)) {
        status = 'Not issued'
    } else if (items.some(item => !item.served)) {
        status = 'Not seen'
    } else {
        status = 'Issued'
    }

    if (order.cancelled) {
        colour = 'red'
        main = 'Cancelled'
    }

    if (order.waived) {
        colour = 'green'
        main = 'Waived'
    } else if (order.closed) {
        colour = 'green'
        main = 'Paid'
    }

    return { main, colour, status }
}

export const log = item => production ? null : console.log(item)

export const useListData = (query, payload, options, name, lim = 10, callback = () => null) => {
    const [list, setList] = useState([])
    const [count, setCount] = useState(0)
    const [page, setPage] = useState(1)
    const [limit] = useState(lim)

    const [{ loading, error, data }, fetch] = usePet(query, {
        ...payload,
        page,
        limit
    }, {
        ...options,
        callback: ({ data }) => {
            setList(data[name ?? query])
            setCount(data.count)
            ws.send('engaged-patients')
            callback(data)
        }
    })

    const navigate = pg => {
        setPage(pg)
        fetch()
    }

    const Paginate = <Pagination total={count} limit={limit} page={page} navigate={navigate} />

    return [{ loading, error, fetch }, { list, count, data }, { navigate, page, limit, Paginate }]
}

export const getGradient = key => {
    let backgrounds = ['grad1', 'grad2', 'grad3', 'grad4']

    let mult = 4

    while (true) {
        if (key < mult)
            return backgrounds[(mult - 1) - key]

        mult += 4
    }
}

export const timeDiff = (time, unit = 'years', from = moment()) => {
    time = moment(time)

    return time > from ? time.diff(from, unit) : from.diff(time, unit)
}

export const patientName = name => name?.trim()?.split(" ")[0] ?? 'the patient'
